import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useState, type FormEvent } from 'react'
import { ApiError } from '../api/client'
import { acceptEscalation, escalationLinkQuery, rejectEscalation, type EscalationLinkReply } from '../api/easeedeskTicketTabs'
import { toast } from '../ui/notify'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'

/**
 * Cele doua pagini pe care le deschide un COLEG din emailul de escaladare
 * interna - `Easeedesk::accept_escalated_ticket` si `reject_escalated_ticket`:
 *
 *   easeedesk/accept_escalated_ticket/<jeton>   ia tichetul pe el
 *   easeedesk/reject_escalated_ticket/<jeton>   il refuza, cu un motiv
 *
 * ADRESELE RAMAN CELE VECHI, cuvant cu cuvant: linkurile le scrie API-ul
 * telefonului (`ph4.api/app/Services/Easeedesk/Notifications.php`) si au plecat
 * deja prin email; fara rutele astea, dupa stergerea portalului ar da 404.
 *
 * Spre deosebire de paginile clientului, astea se deschid IN aplicatie, cu
 * schelet cu tot: omul are cont, si jetonul e legat de contul lui (vezi
 * `EscalationLinks`). Cine nu e logat ajunge la ecranul de intrare si se
 * intoarce la link, exact ca in portal.
 *
 * ACCEPTAREA era in portal un GET care SCHIMBA tichetul la simpla deschidere.
 * Capul v4 e POST, dinadins - previzualizarea clientului de mail si scanerul de
 * securitate deschid linkurile - deci pagina de acceptare arata tichetul si
 * cere o apasare. Dupa ea, drumul e cel vechi: `redirect('easeedesk/ticket/<id>')`
 * cu mesajul "Success. You accepted the escalated ticket".
 *
 * RESPINGEREA e formularul din `ticket/actions/reject_escalation.php`, cu
 * aceleasi clase: `form-horizontal`, eticheta pe `col-sm-3 control-label`,
 * casuta pe `col-sm-6`, "Save" cu `glyphicon-ok`. Comentariul e obligatoriu,
 * ca acolo (`required`).
 *
 * Un jeton care nu deschide nimic (410) si un tichet care nu mai e escaladat
 * catre om (409, "Error. Ticket is no longer escalated to you.") se spun pe
 * pagina, cu drumul spre lista - in portal erau `flashdata` peste
 * `redirect('easeedesk/tickets')`.
 */

/* `<div id="content">`, ca macheta veche: formularul statea direct in el. */
function Frame({ children }: { children: React.ReactNode }) {
  return <Content>{children}</Content>
}

/**
 * Cele doua refuzuri, cu cuvintele portalului si drumul lui: lista de tichete.
 * Nu se face `redirect` de aici - un salt cu mesajul intr-un `flashdata` s-ar
 * pierde la prima reincarcare; pagina spune ce s-a intamplat si lasa omul sa
 * plece cand a citit.
 */
function Refused({ error }: { error: unknown }) {
  const gone = error instanceof ApiError && error.status === 410
  const message = gone
    ? 'An error occured. Please try again.'
    : error instanceof ApiError && error.status === 409
      ? 'Error. Ticket is no longer escalated to you.'
      : error instanceof Error
        ? error.message
        : 'An error occured. Please try again.'

  return (
    <div className={`alert alert-${gone ? 'danger' : 'warning'}`} role="alert">
      <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {message}{' '}
      <a href="/app/easeedesk/tickets">Back to tickets</a>
    </div>
  )
}

function useLanding() {
  const navigate = useNavigate()

  return (reply: EscalationLinkReply, what: 'accepted' | 'rejected') => {
    toast(`Success. You ${what} the escalated ticket`, 'Success', 'success')
    void navigate({ to: '/easeedesk/ticket/$id', params: { id: String(reply.ticket) } })
  }
}

export function AcceptEscalationPage({ token }: { token: string }) {
  const q = useQuery(escalationLinkQuery(token))
  const land = useLanding()
  const [comments, setComments] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<unknown>(null)

  const submit = (ev: FormEvent) => {
    ev.preventDefault()
    setBusy(true)
    acceptEscalation(token, comments.trim())
      .then((reply) => land(reply, 'accepted'))
      .catch((err: unknown) => {
        setFailure(err)
        setBusy(false)
      })
  }

  return (
    <Frame>
      {q.isError || failure !== null ? (
        <Refused error={failure ?? q.error} />
      ) : !q.data ? (
        <Spinner />
      ) : (
        <form className="form-horizontal acceptEscalationForm" onSubmit={submit}>
          <div className="form-group">
            <label className="col-sm-3 control-label">Ticket</label>
            <div className="col-sm-6">
              <p className="form-control-static">
                #{q.data.ticket} {q.data.subject}
              </p>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" htmlFor="acceptEscalationComments">
              Comments
            </label>
            <div className="col-sm-6">
              <textarea id="acceptEscalationComments" className="form-control" name="comments" value={comments} onChange={(ev) => setComments(ev.target.value)}></textarea>
            </div>
          </div>
          <div className="form-group aright">
            <div className="col-sm-9">
              <button className="btn btn-primary" type="submit" name="submit_form" disabled={busy}>
                <span className="fas fa-check" aria-hidden="true"></span> Accept
              </button>
            </div>
          </div>
        </form>
      )}
    </Frame>
  )
}

export function RejectEscalationPage({ token }: { token: string }) {
  const q = useQuery(escalationLinkQuery(token))
  const land = useLanding()
  const [comments, setComments] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<unknown>(null)

  const submit = (ev: FormEvent) => {
    ev.preventDefault()
    if (comments.trim() === '') return
    setBusy(true)
    rejectEscalation(token, comments.trim())
      .then((reply) => land(reply, 'rejected'))
      .catch((err: unknown) => {
        setFailure(err)
        setBusy(false)
      })
  }

  return (
    <Frame>
      {q.isError || failure !== null ? (
        <Refused error={failure ?? q.error} />
      ) : !q.data ? (
        <Spinner />
      ) : (
        <form className="form-horizontal rejectEscalationForm fv-form fv-form-bootstrap" method="post" onSubmit={submit}>
          <input name="token" value={token} type="hidden" readOnly />
          <div className="form-group has-feedback">
            <label className="col-sm-3 control-label" htmlFor="rejectEscalationComments">
              Comments <span className="required">*</span>
            </label>
            <div className="col-sm-6">
              <textarea id="rejectEscalationComments" className="form-control" name="comments" required value={comments} onChange={(ev) => setComments(ev.target.value)}></textarea>
            </div>
          </div>
          <div className="form-group aright">
            <div className="col-sm-9">
              <button className="btn btn-primary" type="submit" name="submit_form" disabled={busy}>
                <span className="fas fa-check" aria-hidden="true"></span> Save
              </button>
            </div>
          </div>
        </form>
      )}
    </Frame>
  )
}
